/**
 * PdfPageNavigator - Thumbnail grid with page selection controls
 */
import { useCallback, useState } from 'react';
import { Document, pdfjs } from 'react-pdf';

import { PdfPageThumbnail } from './PdfPageThumbnail';
import { formatPageSelection, parsePageRange } from './utils';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PdfPageNavigatorProps {
  /** URL of the PDF to display */
  pdfUrl: string;
  /** Set of selected page numbers (1-indexed) */
  selectedPages: Set<number>;
  /** Callback when selection changes */
  onSelectionChange: (pages: Set<number>) => void;
  /** Callback when PDF loads with page count */
  onPdfLoad?: (pageCount: number) => void;
  /** Callback on PDF load error */
  onPdfError?: (error: Error) => void;
}

export const PdfPageNavigator = ({
  pdfUrl,
  selectedPages,
  onSelectionChange,
  onPdfLoad,
  onPdfError,
}: PdfPageNavigatorProps) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [pageRangeInput, setPageRangeInput] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);

  const handleDocumentLoad = useCallback(
    ({ numPages: count }: { numPages: number }) => {
      setNumPages(count);
      setIsLoading(false);
      setLoadError(null);
      onPdfLoad?.(count);
    },
    [onPdfLoad],
  );

  const handleDocumentError = useCallback(
    (error: Error) => {
      setIsLoading(false);
      setLoadError(error.message || 'Failed to load PDF');
      onPdfError?.(error);
    },
    [onPdfError],
  );

  const handleTogglePage = useCallback(
    (pageNumber: number) => {
      const newSelection = new Set(selectedPages);
      if (newSelection.has(pageNumber)) {
        newSelection.delete(pageNumber);
      } else {
        newSelection.add(pageNumber);
      }
      onSelectionChange(newSelection);
    },
    [selectedPages, onSelectionChange],
  );

  const handleClearSelection = useCallback(() => {
    onSelectionChange(new Set());
  }, [onSelectionChange]);

  const handleApplyRange = useCallback(() => {
    if (!pageRangeInput.trim()) return;
    const pages = parsePageRange(pageRangeInput, numPages);
    if (pages.length > 0) {
      onSelectionChange(new Set(pages));
      setPageRangeInput('');
    }
  }, [pageRangeInput, numPages, onSelectionChange]);

  const handleRangeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApplyRange();
    }
  };

  // Error state
  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-500">
        <div className="text-red-500 mb-2">Unable to load PDF</div>
        <span className="text-sm text-gray-400">{loadError}</span>
      </div>
    );
  }

  // Loading indicator component - vertically centered in the container
  const loadingIndicator = (
    <div className="flex flex-col items-center justify-center h-[40vh] text-gray-500">
      <div className="w-8 h-8 border-2 border-gray-300 border-t-indigo-500 rounded-full animate-spin mb-3" />
      <span className="text-sm">Loading document...</span>
    </div>
  );

  // Loading state - Document loads in background while showing spinner
  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <Document
          file={pdfUrl}
          onLoadSuccess={handleDocumentLoad}
          onLoadError={handleDocumentError}
          loading={null}
          className="hidden"
        >
          {null}
        </Document>
        <div className="flex-1 flex items-center justify-center">
          {loadingIndicator}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Selection controls */}
      <div className="flex items-center justify-between gap-4 py-2 border-b border-gray-200 flex-shrink-0">
          <button
            type="button"
            onClick={handleClearSelection}
            disabled={selectedPages.size === 0}
            className="inline-flex items-center justify-center h-9 px-4 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Clear Selection
          </button>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Pages:</span>
            <input
              type="text"
              placeholder="1-3, 5, 7"
              value={pageRangeInput}
              onChange={(e) => setPageRangeInput(e.target.value)}
              onKeyDown={handleRangeKeyDown}
              className="h-9 px-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              style={{ width: '140px' }}
            />
            <button
              type="button"
              onClick={handleApplyRange}
              disabled={!pageRangeInput.trim()}
              className="inline-flex items-center justify-center h-9 px-4 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Apply
            </button>
          </div>

          {/* Selection summary */}
          <div className="text-sm text-gray-500">
            {selectedPages.size === 0 ? (
              <span className="text-gray-400">No pages selected</span>
            ) : (
              <>
                <span className="font-semibold text-gray-900">
                  {selectedPages.size}
                </span>{' '}
                selected
                {selectedPages.size <= 5 && (
                  <span className="text-gray-400 ml-1">
                    ({formatPageSelection(selectedPages)})
                  </span>
                )}
              </>
            )}
          </div>
        </div>

      {/* Scrollable thumbnail grid */}
      <div className="flex-1 overflow-y-auto py-4 min-h-0">
        <Document file={pdfUrl} loading={null}>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 justify-items-center">
            {Array.from({ length: numPages }, (_, i) => i + 1).map(
              (pageNumber) => (
                <PdfPageThumbnail
                  key={pageNumber}
                  pageNumber={pageNumber}
                  isSelected={selectedPages.has(pageNumber)}
                  onToggle={handleTogglePage}
                  width={200}
                />
              ),
            )}
          </div>
        </Document>
      </div>
    </div>
  );
};

export default PdfPageNavigator;
